import { z } from 'zod';
import { Vals } from '~lib';
import { AutomationFilters } from './automation-filters';
import { User } from './user.model';

/**
 * @desc Building blocks shared by every admin-automations entity (openspec change
 * `add-admin-automations`, design §7): identifiers, the recorded actor (D23), the audit history
 * entry (D11), the reason rule, the participant id rule and the permission slugs (D14, ADR-29).
 *
 * Isomorphic by construction: the portal bundles this package, so nothing here imports a Node
 * module, and `ObjectId` appears only as a type in the entity models.
 */
export namespace Automation {
  /**
   * @desc The Ario permission slugs (D14, ADR-29), carried by the `ario` role group
   * (`ario-viewer`: read; `ario-operator`: read, write and run). `canAutomate` grants each action
   * on its slug alone; neither `admin:access` nor `admin:view` implies any of them.
   */
  export const permissions = {
    Read: User.permissions.AutomationsRead,
    Write: User.permissions.AutomationsWrite,
    Run: User.permissions.AutomationsRun,
  } as const;
  export type Permission = Vals<typeof permissions>;

  /** @desc What a caller wants to do, as `canAutomate(caller, action)` is asked it. */
  export const accessActions = ['read', 'write', 'run'] as const;
  export type AccessAction = (typeof accessActions)[number];

  export const MAX_NAME_LENGTH = 120;
  export const MAX_DESCRIPTION_LENGTH = 2_000;
  export const MAX_REASON_LENGTH = 500;
  export const MAX_TAGS = 20;
  export const MAX_TAG_LENGTH = 40;

  /** @desc A Mongo `ObjectId` as it crosses the wire: 24 lowercase or uppercase hex digits. */
  export const objectIdSchema = z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, 'Expected a 24-character hex identifier');

  export const nameSchema = z.string().trim().min(1).max(MAX_NAME_LENGTH);
  export const descriptionSchema = z.string().trim().max(MAX_DESCRIPTION_LENGTH);
  export const tagsSchema = z
    .array(z.string().trim().min(1).max(MAX_TAG_LENGTH))
    .max(MAX_TAGS)
    .refine((tags) => new Set(tags).size === tags.length, 'Tags must be unique');

  /** @desc A version's per-parent number (`restore version N`): a positive whole number. */
  export const versionNumberSchema = z.number().int().positive();

  /** @desc A non-empty, bounded reason — required for disable, cancel, restore and archive (D11). */
  export const reasonSchema = z.string().trim().min(1).max(MAX_REASON_LENGTH);

  /**
   * @desc Participant id: `p_` followed by 8–32 lowercase hex digits.
   *
   * Participant ids become field names (`contexts.agents.<id>`, `activeTurns.<id>`), and a Mongo
   * field name must not contain `.` or start with `$`; a fixed alphabet makes every id a safe
   * path segment by construction, so no store has to escape one.
   */
  export const PARTICIPANT_ID_PATTERN = /^p_[0-9a-f]{8,32}$/;
  export type ParticipantId = `p_${string}`;

  export const isParticipantId = (value: unknown): value is ParticipantId =>
    typeof value === 'string' && PARTICIPANT_ID_PATTERN.test(value);

  export const participantIdSchema = z
    .string()
    .regex(PARTICIPANT_ID_PATTERN, 'Expected a participant id of the form p_<hex>')
    .transform((value) => value as ParticipantId);

  /** @desc Mint a participant id: `p_` and 16 hex digits (64 random bits, Web Crypto). */
  export const newParticipantId = (): ParticipantId => {
    const bytes = crypto.getRandomValues(new Uint8Array(8));
    return `p_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  };

  /** @desc The literal recorded for writes the scheduler makes (D11, D23). */
  export const SCHEDULER = 'scheduler' as const;

  /**
   * @desc Who made a write (D23): an admin's internal user id; the authoring agent acting for the
   * conversation's owner; or the scheduler.
   */
  export type AgentActor = { userId: string; via: 'agent'; participantId: ParticipantId };
  export type Actor = typeof SCHEDULER | AgentActor | string;

  export const actorSchema = z.union([
    z.literal(SCHEDULER),
    z.object({
      userId: z.string().min(1),
      via: z.literal('agent'),
      participantId: participantIdSchema,
    }),
    z.string().min(1),
  ]);

  export const isAgentActor = (actor: Actor): actor is AgentActor => typeof actor === 'object';

  /** @desc The admin accountable for a write, or `null` for the scheduler. */
  export const actorUserId = (actor: Actor): string | null => {
    if (typeof actor === 'object') return actor.userId;
    return actor === SCHEDULER ? null : actor;
  };

  /** @desc Mutating operator actions recorded in `history[]` (D11, spec "Immutable Operator Audit History"). */
  export const actions = {
    Create: 'create',
    Update: 'update',
    Save: 'save',
    Accept: 'accept',
    Discard: 'discard',
    Restore: 'restore',
    Enable: 'enable',
    Disable: 'disable',
    Archive: 'archive',
    Cancel: 'cancel',
    Retry: 'retry',
    Finalise: 'finalise',
    Rotate: 'rotate',
    /** @desc Version-level (task 4.14): the reviewer's findings attached to a collector draft. */
    AttachReview: 'attach-review',
    /** @desc Version-level: a test run linked to the collector draft it exercised. */
    RecordTestRun: 'record-test-run',
    /** @desc Version-level: the authoring plan mirrored onto a workflow draft (D25). */
    SetPlan: 'set-plan',
    /** @desc A host's robots override granted (`automationSourcePolicies`, ADR-30, D22). */
    GrantOverride: 'grant-override',
    /** @desc A host's robots override revoked: the host reads as `respect` again. */
    RevokeOverride: 'revoke-override',
  } as const;
  export type Action = Vals<typeof actions>;

  /** @desc Actions that SHALL carry a non-empty reason (D11). */
  export const reasonRequiredActions: ReadonlySet<Action> = new Set<Action>([
    actions.Disable,
    actions.Cancel,
    actions.Restore,
    actions.Archive,
    actions.GrantOverride,
    actions.RevokeOverride,
  ]);

  export const requiresReason = (action: Action): boolean => reasonRequiredActions.has(action);

  /**
   * @desc Every reason `action` cannot be recorded with `reason`, as sentences; empty when valid.
   * Shared by the services and the portal's confirmation dialogs.
   */
  export function reasonProblems(action: Action, reason: string | undefined): string[] {
    const trimmed = reason?.trim() ?? '';
    const found: string[] = [];
    if (requiresReason(action) && !trimmed) found.push(`A reason is required to ${action}.`);
    if (trimmed.length > MAX_REASON_LENGTH) {
      found.push(`The reason must be at most ${MAX_REASON_LENGTH} characters.`);
    }
    return found;
  }

  /**
   * @desc One audited write (D11): immutable once appended. `before`/`after` are snapshots of the
   * fields the action changed (`null` on create / when nothing remains); `traceId` is the
   * request's trace id, `null` when no span was active.
   */
  export type HistoryEntry<T, D extends string | Date = Date> = {
    at: D;
    actor: Actor;
    action: Action;
    reason?: string;
    before: T | null;
    after: T | null;
    traceId: string | null;
  };

  /** @desc Identity statuses shared by workflows and collectors (§7). Deletion is `archived`. */
  export const entityStatuses = AutomationFilters.entityStatuses;
  export type EntityStatus = Vals<typeof entityStatuses>;

  /**
   * @desc Statuses of the entities with no draft/ready lifecycle — schedules, credentials and
   * authoring conversations: live, or archived (soft-deleted, D11).
   *
   * One archive representation across every automation entity (task 4.14): `status:
   * 'archived'`, the §7 shape workflows and collectors already had, rather than a separate
   * `archived: boolean` on some. A soft delete is then the same field, the same filter
   * (`status: { $ne: 'archived' }`) and the same {@link isArchived} test everywhere.
   */
  export const lifecycleStatuses = {
    Active: 'active',
    Archived: 'archived',
  } as const;
  export type LifecycleStatus = Vals<typeof lifecycleStatuses>;

  /** @desc Whether an automation entity is archived — workflow, collector, schedule, credential or conversation. */
  export const isArchived = (doc: { status?: EntityStatus | LifecycleStatus }): boolean =>
    doc.status === entityStatuses.Archived;

  /** @desc Version statuses (D10): only `draft` is writable; `accepted` is immutable. */
  export const versionStatuses = {
    Draft: 'draft',
    Accepted: 'accepted',
    Discarded: 'discarded',
  } as const;
  export type VersionStatus = Vals<typeof versionStatuses>;

  /** @desc A Mongo-held lease on in-flight work (a run, or a participant's turn). */
  export type Lease<D extends string | Date = Date> = {
    /** @desc Process identifier of the holder. */
    holder: string;
    acquiredAt: D;
    heartbeatAt: D;
    expiresAt: D;
  };
}
